import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { CircuitResponse } from '@/types/circuits/CircuitResponse';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { EstimateResponse } from '@/types/estimates/EstimateResponse';
import { SupportedLocale } from '@/types/supported-locale.type';
import { Translator } from '@/types/translator.type';
import { sanitizeDeltaPrice } from '@/utils/circuits/prices/formatPrice';
import { loadCircuitDefaultValues } from '../circuits/loadCircuitDefaultValues';
import {
  AgencyFlexibilityVisibilityFlag,
  shouldMaskAgencyFlexibilityFieldForViewer,
} from './shouldHideAgencyFlexibilityContent';
import {
  buildStandardHotelDefaultsByStepId,
  isDefaultStandardHotelOption,
} from './isDefaultStandardHotelOption';
import { BenefitType } from '@/types/benefits/benefitTypes';

const ESTIMATE_CIRCUIT_FIELD_FLAG: Partial<
  Record<
    keyof Omit<
      EstimateResponse['EstimateCircuit'],
      | 'circuit'
      | 'selectedOptions'
      | 'daysTitles'
      | 'daysDescriptions'
      | 'paragraphStates'
      | 'clientFrozenValues'
    >,
    AgencyFlexibilityVisibilityFlag
  >
> = {
  summary: 'showSummaryAndImportantPoints',
  importantPoints: 'showSummaryAndImportantPoints',
  priceIncludes: 'showPriceIncludesExcludes',
  priceExcludes: 'showPriceIncludesExcludes',
  modificationsConditions: 'showModificationCancellationConditions',
  cancellationConditions: 'showModificationCancellationConditions',
};

export const loadEstimateDefaultValues = ({
  estimateResponse,
  t,
  l: language,
}: {
  estimateResponse: EstimateResponse;
  t: Translator;
  l?: SupportedLocale;
}): EstimateFormData => {
  const estimateLanguage = estimateResponse.language
    ? (estimateResponse.language.toLowerCase() as SupportedLocale)
    : undefined;

  const translationLanguage = estimateLanguage ?? language;

  const circuitTranslations = estimateResponse.EstimateCircuit.circuit
    .translations as CircuitResponse['translations'] | undefined;
  const estimateTranslations = estimateResponse.translations as
    | CircuitResponse['translations']
    | undefined;

  const translation =
    translationLanguage != null
      ? (estimateTranslations?.[translationLanguage] ??
        circuitTranslations?.[translationLanguage])
      : undefined;

  const hasStoredText = (value: string | null | undefined): value is string =>
    value != null && value.trim() !== '';

  const getCustomFieldOtherwiseCircuitField = (
    field: keyof Omit<
      EstimateResponse['EstimateCircuit'],
      | 'circuit'
      | 'selectedOptions'
      | 'daysTitles'
      | 'daysDescriptions'
      | 'paragraphStates'
      | 'clientFrozenValues'
    >,
  ): string | undefined => {
    const visibilityFlag = ESTIMATE_CIRCUIT_FIELD_FLAG[field];
    if (
      visibilityFlag &&
      shouldMaskAgencyFlexibilityFieldForViewer(
        estimateResponse,
        visibilityFlag,
      )
    ) {
      return undefined;
    }

    const originalField =
      field === 'modificationsConditions' ? 'modificationConditions' : field;

    const estimateValue = estimateResponse.EstimateCircuit[field];

    const circuitValue =
      estimateResponse.EstimateCircuit.circuit[originalField] ?? undefined;

    const fallbackValue =
      (hasStoredText(estimateValue) ? estimateValue : undefined) ??
      (hasStoredText(translation?.[originalField])
        ? translation[originalField]
        : undefined) ??
      (hasStoredText(circuitValue) ? circuitValue : undefined);

    return fallbackValue;
  };

  const parsedCircuit = loadCircuitDefaultValues({
    circuit: {
      ...estimateResponse.EstimateCircuit.circuit,
      nbParticipants:
        estimateResponse.adults.length + estimateResponse.children.length,
      circuitDays: estimateResponse.EstimateCircuit.circuit.circuitDays
        .toSorted(({ day: day1 }, { day: day2 }) => day1 - day2)
        .map((circuitDay, indexDay) => {
          return {
            ...circuitDay,
            description:
              estimateResponse.EstimateCircuit?.daysDescriptions?.[indexDay] ??
              translation?.circuitDays?.[indexDay]?.description ??
              circuitDay.description,
            stepTitle:
              estimateResponse.EstimateCircuit.daysTitles?.[indexDay] ??
              translation?.circuitDays?.[indexDay]?.stepTitle ??
              circuitDay.stepTitle,
          };
        }),
      translations:
        estimateResponse.translations as CircuitResponse['translations'],
    },
    t,
    l: language ?? estimateLanguage,
  }) as CircuitFormDataWithTranslations;

  const standardHotelDefaultsByStepId = buildStandardHotelDefaultsByStepId(
    estimateResponse.EstimateCircuit.circuit,
  );

  return {
    marginRate: estimateResponse.marginRate ?? undefined,
    adults: estimateResponse.adults.map((adult) => ({
      firstName: adult.firstName ?? undefined,
      lastName: adult.lastName ?? undefined,
    })),
    children: estimateResponse.children.map((child) => ({
      firstName: child.firstName ?? undefined,
      lastName: child.lastName ?? undefined,
      birthDate: child.birthDate ? new Date(child.birthDate) : undefined,
    })),
    circuit: {
      ...parsedCircuit,
      summary: getCustomFieldOtherwiseCircuitField('summary'),
      importantPoints: getCustomFieldOtherwiseCircuitField('importantPoints'),
      priceIncludes: getCustomFieldOtherwiseCircuitField('priceIncludes'),
      priceExcludes: getCustomFieldOtherwiseCircuitField('priceExcludes'),
      modificationsConditions: getCustomFieldOtherwiseCircuitField(
        'modificationsConditions',
      ),
      cancellationConditions: getCustomFieldOtherwiseCircuitField(
        'cancellationConditions',
      ),
    },
    internationalFlightsPrice: estimateResponse.internationalFlightsPrice ?? 0,
    internationalFlightsNonMarginedTaxes:
      estimateResponse.internationalFlightsNonMarginedTaxes ?? undefined,
    internationalFlightsMarginRate:
      estimateResponse.internationalFlightsMarginRate ?? undefined,
    marginRateExistsForInternationalFlights:
      estimateResponse.marginRateExistsForInternationalFlights !== null
        ? estimateResponse.marginRateExistsForInternationalFlights
          ? 'true'
          : 'false'
        : undefined,
    agencyMarginRate: estimateResponse.agencyMarginRate ?? undefined,
    agencyInternationalFlightsPrice:
      estimateResponse.agencyInternationalFlightsPrice ?? undefined,
    agencyInternationalFlightsNonMarginedTaxes:
      estimateResponse.agencyInternationalFlightsNonMarginedTaxes ?? undefined,
    agencyInternationalFlightsMarginRate:
      estimateResponse.agencyInternationalFlightsMarginRate ?? undefined,
    agencyMarginRateExistsForInternationalFlights:
      estimateResponse.agencyMarginRateExistsForInternationalFlights != null
        ? estimateResponse.agencyMarginRateExistsForInternationalFlights
          ? 'true'
          : 'false'
        : undefined,
    agencyAdditionalMarginType:
      estimateResponse.agencyAdditionalMarginType ?? 'FIXED',
    agencyAdditionalMarginValue:
      estimateResponse.agencyAdditionalMarginValue ?? undefined,
    customParagraphTitle: estimateResponse.customParagraphTitle ?? undefined,
    customParagraphDescription:
      estimateResponse.customParagraphDescription ?? undefined,
    mainContact: {
      firstName:
        estimateResponse.contact?.firstName ??
        estimateResponse.project.client.mainContact.firstName ??
        '',
      lastName:
        estimateResponse.contact?.lastName ??
        estimateResponse.project.client.mainContact.lastName ??
        '',
      email:
        estimateResponse.contact?.email ??
        estimateResponse.project.client.mainContact.email ??
        undefined,
      phoneNumber:
        estimateResponse.contact?.phoneNumber ??
        estimateResponse.project.client.mainContact.phoneNumber ??
        undefined,
      address: estimateResponse.contact?.address ?? undefined,
      postalCode: estimateResponse.contact?.postalCode ?? undefined,
      city: estimateResponse.contact?.city ?? undefined,
    },
    selectedEstimateOptions:
      estimateResponse.EstimateCircuit.selectedOptions &&
      estimateResponse.EstimateCircuit.selectedOptions.length > 0
        ? estimateResponse.EstimateCircuit.selectedOptions.reduce(
            (acc, { EstimateOptionInfo, circuitDayStepId }) => {
              const optionType = EstimateOptionInfo.type as BenefitType;
              const loadedOption = {
                deltaPrice: sanitizeDeltaPrice(EstimateOptionInfo.deltaPrice),
                type: optionType,
                benefitId: EstimateOptionInfo.benefitId,
                carCategoryId: EstimateOptionInfo.carCategoryId ?? undefined,
                hotelCategoryId:
                  EstimateOptionInfo.hotelCategoryId ?? undefined,
                hotelPensionType:
                  EstimateOptionInfo.hotelPensionType ?? undefined,
                roomCombination:
                  EstimateOptionInfo.roomCombination &&
                  EstimateOptionInfo.roomCombination.length > 0
                    ? EstimateOptionInfo.roomCombination.flatMap(
                        (
                          { nbAdults, nbChildren, nbAdolescents },
                          indexCombination,
                        ) =>
                          Array(
                            EstimateOptionInfo.roomCounts[indexCombination],
                          ).fill([nbAdults, nbChildren, nbAdolescents]),
                      )
                    : undefined,
                carCategoryOptions: {
                  insurances:
                    EstimateOptionInfo.carCategoryInsurances ?? undefined,
                  options: EstimateOptionInfo.carCategoryOptions ?? undefined,
                  taxes: EstimateOptionInfo.carCategoryTaxes ?? undefined,
                  oneWayFees: EstimateOptionInfo.carCategoryOneWayFees
                    ? EstimateOptionInfo.carCategoryOneWayFees.map(
                        (params) => ({ ...params, isRequired: true }),
                      )
                    : undefined,
                },
              };

              // Auto-persisted standard hotel (default category/pension) is already
              // included in the circuit price — do not surface a ghost surcharge.
              if (
                isDefaultStandardHotelOption(
                  loadedOption,
                  standardHotelDefaultsByStepId.get(circuitDayStepId),
                )
              ) {
                loadedOption.deltaPrice = 0;
              }

              return {
                ...acc,
                [circuitDayStepId]: loadedOption,
              };
            },
            {},
          )
        : {},
  };
};
